import { useCallback, useRef, useState } from 'react';
import type {
  InterventionTemplateDetail,
  PatientInterventionDetail,
} from '../../services/api/types.gen';
import { getInterventionsForPatientGoal } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';

interface UseGoalCardInterventionsArgs {
  visitId: string;
  patientGoalId: string;
  initialInterventions: PatientInterventionDetail[];
  onExpand: () => void;
}

export function useGoalCardInterventions({
  visitId,
  patientGoalId,
  initialInterventions,
  onExpand,
}: UseGoalCardInterventionsArgs) {
  const { error: showError } = useToast();
  const [freeTextInterventionKeys, setFreeTextInterventionKeys] = useState<
    string[]
  >([]);
  const nextKeyRef = useRef(0);
  const [pendingTemplates, setPendingTemplates] = useState<
    InterventionTemplateDetail[]
  >([]);
  const [savedInterventions, setSavedInterventions] =
    useState<PatientInterventionDetail[]>(initialInterventions);

  const refreshInterventions = useCallback(() => {
    getInterventionsForPatientGoal({
      path: { visit_id: visitId, patient_goal_id: patientGoalId },
      throwOnError: true,
    })
      .then(response => {
        setSavedInterventions(response.data ?? []);
      })
      .catch(() => {
        showError('Failed to load interventions');
      });
  }, [visitId, patientGoalId, showError]);

  const handleAddInterventions = useCallback(
    (templates: InterventionTemplateDetail[]) => {
      setPendingTemplates(prev => {
        const existingIds = new Set(prev.map(t => t.id));
        return [...prev, ...templates.filter(t => !existingIds.has(t.id))];
      });
      onExpand();
    },
    [onExpand]
  );

  const handleInterventionDiscarded = useCallback((templateId: string) => {
    setPendingTemplates(prev => prev.filter(t => t.id !== templateId));
  }, []);

  const handleFreeTextInterventionSaved = useCallback(
    (key: string) => {
      setFreeTextInterventionKeys(prev => prev.filter(k => k !== key));
      refreshInterventions();
    },
    [refreshInterventions]
  );

  const handleInterventionSaved = useCallback(
    (templateId: string) => {
      setPendingTemplates(prev => prev.filter(t => t.id !== templateId));
      refreshInterventions();
    },
    [refreshInterventions]
  );

  const handleCancelFreeText = useCallback((key: string) => {
    setFreeTextInterventionKeys(prev => prev.filter(k => k !== key));
  }, []);

  const handleUseFreeText = useCallback(() => {
    const key = `free-text-${nextKeyRef.current++}`;
    setFreeTextInterventionKeys(prev => [...prev, key]);
    onExpand();
  }, [onExpand]);

  const handleSaveForTemplate = useCallback(
    (templateId: string) => () => {
      handleInterventionSaved(templateId);
    },
    [handleInterventionSaved]
  );

  const handleCancelForTemplate = useCallback(
    (templateId: string) => () => {
      handleInterventionDiscarded(templateId);
    },
    [handleInterventionDiscarded]
  );

  return {
    freeTextInterventionKeys,
    pendingTemplates,
    savedInterventions,
    handleAddInterventions,
    handleFreeTextInterventionSaved,
    handleCancelFreeText,
    handleUseFreeText,
    handleSaveForTemplate,
    handleCancelForTemplate,
    refreshInterventions,
  };
}
